import * as React from 'react';
import { Form, FormComponent } from './Form';
import { whenEscKey, whenEnterKey, formatValue, Format, formatAsElementId } from './Utils';
import { Res } from 'core/api';
import classnames from 'classnames';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';
import { Dialog, DialogActions, showCustom } from './Dialog';
import Button from './Button';
import { TextBox } from './TextBox';
import * as TagUtils from './TagUtils';
import { createTagsFilter } from './createTagsFilter';

interface CheckBoxItemProps extends React.HTMLProps<CheckBoxItem> {
	inputId: string;
	label: string;
	imageContent: string;
	checked: boolean;
	item: any;
	onCheckedChanged: (option: any, value: boolean) => void;
	formId?: string;
}

class CheckBoxItem extends React.Component<CheckBoxItemProps, { checked: boolean }> {

	constructor(props: CheckBoxItemProps) {
		super(props);

		this.state = { checked: this.props.checked };
	}

	componentDidUpdate() {
		if (this.state.checked !== this.props.checked) {
			this.setState({ checked: this.props.checked });
		}
	}

	render() {

		const onChange = (e: any) => {
			const checked = e.target.checked;
			this.setState({ checked }, () => this.props.onCheckedChanged(this.props.item, checked));
		};

		const onImgClicked = () => {
			const checked = !this.state.checked;
			this.setState({ checked: !this.state.checked }, () => this.props.onCheckedChanged(this.props.item, checked));
		};

		const image = this.props.imageContent.length
			? <span onClick={onImgClicked} className='m-r-md'><img src={`data:image/*;base64,${this.props.imageContent}`} style={{ width: 24, height: 24, border: 'none' }} /></span>
			: null;

		return <div className='checkbox abc-checkbox'>
			{image}
			<span className='m-l-sm'>
				<input key={this.props.key} id={this.props.inputId} type='checkbox' checked={this.state.checked} onChange={onChange} />
				<label htmlFor={this.props.inputId}>{this.props.label}</label>
			</span>
		</div>;
	}
}

interface CheckBoxListProps extends React.HTMLAttributes<CheckBoxList> {
	format: Format;
	getItemImageContent: (item: any) => string;
	items: any[];
	initialSelectedItems: any[];
	allowedTags?: string[]
}

interface CheckBoxListState {
	filter?: string;
	selectedItems?: any[];
	checkUnCheckAll?: boolean;
	tags: string[];
}

// Este export está para los tests
export class CheckBoxList extends React.Component<CheckBoxListProps, CheckBoxListState> {

	constructor(props: CheckBoxListProps) {
		super(props);

		this.state = this.getInitialState(props);
	}

	getInitialState(nextProps: CheckBoxListProps): CheckBoxListState {
		return {
			filter: '',
			tags: [],
			selectedItems: nextProps.initialSelectedItems,
			checkUnCheckAll: nextProps.initialSelectedItems.length === nextProps.items.length
		};
	}

	getFilteredItems(filter: string): any[] {
		return TagUtils.filterByTags(this.props.items, this.state.tags)
			.filter(x => {
				const formatedLabel = formatValue(x, this.props.format);
				return formatedLabel.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
			});
	}

	getSelectedItems() {
		return this.state.selectedItems || [];
	}

	createList() {

		const removeSelectedOption = (option: any) => {

			// Lo hacemos en base al format porque los existing y los selectedItems
			// que me pasan no tienen porque ser la misma referencia
			const selectedItems = [...this.getSelectedItems()];

			const optionToDelete = selectedItems
				.find(x => formatValue(x, this.props.format).indexOf(formatValue(option, this.props.format)) >= 0);

			selectedItems.remove(optionToDelete);
			this.setState({ selectedItems });
		};

		const addSelectedOption = (option: any) => {
			const selectedItems = [...this.getSelectedItems()];
			selectedItems.push(option);
			this.setState({ selectedItems });
		};

		const onOptionChanged = (option: any, checked: boolean) => {
			if (checked)
				addSelectedOption(option);
			else
				removeSelectedOption(option);
		};

		const items = this.getFilteredItems(this.state.filter || '')
			.sortBy(x => formatValue(x, this.props.format))
			.map((x, index) => {
				const label = formatValue(x, this.props.format);
				const imageContent = this.props.getItemImageContent(x);
				const selectedFormattedValues = this.getSelectedItems().map(x => formatValue(x, this.props.format));
				return <li key={index} className='list-group-item'>
					{<CheckBoxItem label={label} inputId={label} checked={selectedFormattedValues.indexOf(label) >= 0} item={x} onCheckedChanged={onOptionChanged} imageContent={imageContent} />}
				</li>;
			});

		return <ul className='multi-select-input-options-list list-group'>
			{items}
		</ul>;
	}

	render() {

		const onFilterChange = (value: string) => this.setState({ filter: value });
		const onCheckUnCheckAll = (e: any) => {
			const checked = e.target.checked;
			if (checked)
				this.setState({ selectedItems: [...this.props.items], checkUnCheckAll: checked });
			else
				this.setState({ selectedItems: [], checkUnCheckAll: checked });
		};

		const onTagsChanged = (values: string[]) => {
			this.setState({ tags: values });
		};

		return <div>
			{/* Uso un Form sólo para maquetar. Realmente nunca haré updateModel, ni remount, ni nada parecido porque estoy escuchando los onValueChange*/}
			<Form<CheckBoxListState> model={this.state}>
				<TextBox<CheckBoxListState>
					autoFocus
					autoComplete='off'
					linkTo={x => x.filter}
					onValueChange={onFilterChange}
					placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()} />
				{
					createTagsFilter<CheckBoxListState>({
						linkTo: x => x.tags,
						maybeTaggableItems: this.props.items,
						allowedTags: this.props.allowedTags,
						onValueChanged: onTagsChanged
					})
				}
			</Form>
			<div className='m-b-xs'>
				{this.createList()}
				<div className='checkbox abc-checkbox'>
					<input id='checkUnCheckAll' type='checkbox' checked={this.state.checkUnCheckAll} onChange={onCheckUnCheckAll} />
					<label htmlFor='checkUnCheckAll'>{Res.WA.WA_MULTI_SELECT_LIST_MARK_UNCHECK_ALL_CHK()}</label>
				</div>
			</div>
		</div>;
	}
}

interface MultiSelectDialogProps extends React.HTMLAttributes<MultiSelectDialog> {
	title: string;
	actions?: DialogActions<any[]>;
	itemList: CheckBoxListProps;
}

class MultiSelectDialog extends React.Component<MultiSelectDialogProps, EmptyState> {

	private itemList = React.createRef<CheckBoxList>();

	constructor(props: MultiSelectDialogProps) {
		super(props);
	}

	getSelectedOptions() {
		return this.itemList.current!.getSelectedItems();
	}

	render() {

		const onAccept = () => this.props.actions && this.props.actions.closeWith(this.getSelectedOptions());
		const onCancel = () => this.props.actions && this.props.actions.cancel();

		const onDefaultKeyDown = (e: React.KeyboardEvent<any>) => {
			whenEnterKey(onAccept)(e);
			whenEscKey(onCancel)(e);
		};

		return <Dialog onKeyDown={onDefaultKeyDown}>
			<div className='modal-content'>
				<div className='modal-header'>
					<h4 className='modal-title'>{this.props.title}</h4>
				</div>
				<div className='modal-body'>
					<CheckBoxList ref={this.itemList} {... this.props.itemList} />
				</div>

				<div className='modal-footer'>
					<Button buttonStyle='info' onClick={onAccept}>{Res.Common.OK_BTN()}</Button>
					<Button onClick={onCancel}>{Res.Common.CANCEL_BTN()}</Button>
				</div>
			</div>
		</Dialog>;
	}
}

interface MultiSelectInputProps<TModel, TItem = any> extends React.HTMLProps<MultiSelectInput<TModel>> {
	label?: string;
	items: TItem[];
	boundValue?: TItem[];
	linkTo: Binding.PropertyPath<TModel>;
	format?: Format;
	getItemImageContent?: (item: TItem) => string;
	dialogTitle: string;
	fullSelectionText: string;
	formId?: string;
	allowedTags?: string[],
	onSelectedItemsChanged?: (items: TItem[]) => void;
}

interface MultiSelectInputState {
	errorMsg?: string;
	selectedItems?: any[];
}

export class MultiSelectInput<TModel, TItem = any> extends React.Component<MultiSelectInputProps<TModel, TItem>, MultiSelectInputState> implements FormComponent {

	constructor(props: MultiSelectInputProps<TModel>) {

		super(props);

		if (!(props.boundValue instanceof Array))
			throw new Error('MultiSelectInput esperaba un boundValue de tipo Array');

		this.state = this.createStateFromProps(props);
	}

	createStateFromProps(props: MultiSelectInputProps<TModel>) {
		return {
			errorMsg: '',
			selectedItems: TagUtils.filterByTags(props.boundValue ?? [], props.allowedTags ?? [])
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<any[]> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.getSelectedItems());
	}

	getItemsDescription(): string {

		const selectedItems = this.getSelectedItems();

		if (this.props.items.length === 0 || selectedItems.length === 0)
			return this.props.placeholder || '';

		return selectedItems.length === this.getAllowedItems().length
			? this.props.fullSelectionText ?? ''
			: selectedItems.map((x: any) => formatValue(x, this.props.format)).join(', ');
	}

	getAllowedItems() {
		return TagUtils.filterByTags(this.props.items, this.props.allowedTags ?? []);
	}

	getSelectedItems() {
		return this.state.selectedItems || [];
	}

	protected buildSelectItemsDialog() {
		const listProps: CheckBoxListProps = {
			items: this.getAllowedItems(),
			initialSelectedItems: this.getSelectedItems(),
			format: this.props.format,
			getItemImageContent: this.props.getItemImageContent || (() => ''),
			allowedTags: this.props.allowedTags
		};

		return <MultiSelectDialog title={this.props.dialogTitle} itemList={listProps} />;
	}

	protected showSelectItemsDialog(): Promise<Result<any[]>> {
		return showCustom<any[]>(this.buildSelectItemsDialog());
	}

	render() {

		const divRef = React.createRef<HTMLDivElement>();

		const onShowItems = async () => {
			divRef.current?.focus();
			await this.showSelectItemsDialog().then(result => {
				if (result.ok) this.setState({ selectedItems: result.value }, () => this.props.onSelectedItemsChanged?.(result.value));
			});
		};

		const inputId = formatAsElementId(this.getPath(), this.props.formId || '');
		const selectionDescription = this.getItemsDescription();

		const onDivKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === ' ' || e.key === 'F3') {
				e.preventDefault();
				e.stopPropagation();
				await onShowItems();
			}
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			<label className='col-sm-4 control-label' htmlFor={inputId}>
				{this.props.label || ''}
			</label>

			<div className='col-sm-8' onClick={onShowItems} onKeyDown={onDivKeyDown}>
				<div className='input-group' ref={divRef} tabIndex={0} style={{ borderRadius: 4 }}>
					<input type='text' id={inputId} className='form-control' value={selectionDescription} readOnly style={{ background: 'white', cursor: 'pointer' }} tabIndex={-1} />
					<span className='input-group-btn'>
						<Button tabIndex={-1}><i className='text-info fa fa-search'></i></Button>
					</span>
				</div>
				<ErrorBlock msg={this.state.errorMsg} />
			</div>
		</div>;
	}
}
export type MultiSelectInputCtor<T> = new (props: MultiSelectInputProps<T>) => React.Component<MultiSelectInputProps<T>, MultiSelectInputState>;
